/**
 * Single label block: start page, end page, optional name. At least one row required in the parent form.
 */
import React, { ChangeEvent } from 'react';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import { LabelRangeDraft } from './types';
import styles from './labelSeparation.styles';

type LabelRangeRowProps = {
  index: number;
  range: LabelRangeDraft;
  canRemove: boolean;
  disabled?: boolean;
  /* eslint-disable no-unused-vars -- type-only callback signatures */
  onFieldChange: (field: keyof Omit<LabelRangeDraft, 'id'>, value: string) => void;
  /* eslint-enable no-unused-vars */
  onRemove: () => void;
};

const LabelRangeRow = ({
  index,
  range,
  canRemove,
  disabled = false,
  onFieldChange,
  onRemove
}: LabelRangeRowProps) => {
  const handleChange =
    (field: keyof Omit<LabelRangeDraft, 'id'>) => (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(field, event.target.value);
    };

  return (
    <Box sx={styles.rangeRowCard}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={styles.rangeRowHeader}>
        <Typography sx={styles.rangeRowLabel}>{`Label ${index + 1}`}</Typography>
        <IconButton
          aria-label={`Remove label ${index + 1}`}
          size='small'
          color='secondary'
          disabled={!canRemove || disabled}
          onClick={onRemove}>
          <RemoveIcon />
        </IconButton>
      </Stack>

      <Stack spacing={2}>
        <TextField
          required
          type='number'
          label='Start page'
          value={range.startPage}
          disabled={disabled}
          inputProps={{ min: 1 }}
          onChange={handleChange('startPage')}
        />
        <TextField
          required
          type='number'
          label='End page'
          value={range.endPage}
          disabled={disabled}
          inputProps={{ min: 1 }}
          onChange={handleChange('endPage')}
        />
        <TextField
          label='Label name'
          value={range.labelName}
          disabled={disabled}
          onChange={handleChange('labelName')}
        />
      </Stack>
    </Box>
  );
};

LabelRangeRow.defaultProps = {
  disabled: false
};

export default LabelRangeRow;
